import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { Reveal, MaskLine } from "./Reveal";

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-12"];

const CollectionCard = ({ c, i, image, className }) => (
  <Reveal delay={(i % 2) * 0.1} className={className}>
    <Link
      to={`/parfums?collection=${c.slug}`}
      data-testid={`collection-card-${c.slug}`}
      className="group relative block h-[400px] overflow-hidden rounded-2xl bg-cocoa sm:h-[520px]"
    >
      {image ? (
        <img
          src={image}
          alt={c.name}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center font-serif text-8xl text-ivory/20">V</div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-espresso/90 via-espresso/25 to-transparent" />
      <div className="absolute inset-0 flex flex-col justify-between p-7 sm:p-9">
        <div className="flex items-start justify-between">
          <span className="font-serif text-lg italic text-ivory/80">0{i + 1}</span>
          <span className="rounded-full border border-ivory/25 px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] text-ivory/80">
            {c.count} {c.count > 1 ? "créations" : "création"}
          </span>
        </div>
        <div>
          <h3 className="font-serif text-3xl leading-tight text-ivory sm:text-4xl">{c.name}</h3>
          <p className="mt-2 text-sm text-ivory/70">{c.tagline}</p>
          <span className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-champagne">
            Découvrir
            <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </span>
        </div>
      </div>
    </Link>
  </Reveal>
);

export const Collections = () => {
  const { perfumes, collections } = useShop();
  const imgFor = (slug) => perfumes.find((p) => (p.collections || []).includes(slug))?.image_url || "";

  return (
    <section id="collections" className="bg-ivory py-24 sm:py-32">
      <div className="wrap">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal>
              <p className="eyebrow flex items-center gap-4 text-champagne">
                <span className="h-px w-10 bg-champagne" />
                Les collections
              </p>
            </Reveal>
            <MaskLine className="mt-5">
              <h2 className="font-serif text-4xl leading-[1.02] text-espresso sm:text-5xl lg:text-6xl">
                Un vestiaire olfactif
                <br />
                <em className="italic text-taupe">complet</em>
              </h2>
            </MaskLine>
          </div>
          <Reveal delay={0.15}>
            <Link
              data-testid="collections-view-all"
              to="/parfums"
              className="group flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-espresso/80 transition-colors duration-300 hover:text-champagne"
            >
              Toute la collection
              <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 lg:grid-cols-12">
          {collections.map((c, i) => (
            <CollectionCard key={c.slug} c={c} i={i} image={imgFor(c.slug)} className={SPANS[i % 4]} />
          ))}
        </div>
      </div>
    </section>
  );
};
